import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { Directory } from '@mnc/contracts';
import { api } from '../../lib/api';
import { pick } from '../../i18n';
import { SearchableSelect } from '../ui';

/** Multi-select of active users from the directory (granted to every staff member). */
export function UserPicker({ value, onChange, disabled, exclude = [] }: { value: string[]; onChange: (ids: string[]) => void; disabled?: boolean; exclude?: string[] }) {
  const { t, i18n } = useTranslation();
  const dir = useQuery({ queryKey: ['users', 'directory'], queryFn: () => api.get<Directory>('/api/users/directory'), staleTime: 60_000 });
  const options = useMemo(
    () => (dir.data?.items ?? []).filter((u) => !exclude.includes(u.id)).map((u) => ({ value: u.id, label: pick(i18n.language, u.display_name_en, u.display_name_ar) })),
    [dir.data, i18n.language, exclude],
  );
  return <SearchableSelect multiple disabled={disabled} value={value} onChange={(v) => onChange(v as string[])} options={options} placeholder={t('records.pickPeople')} />;
}

export function useDirectoryNames(): (id: string) => string {
  const { i18n } = useTranslation();
  const dir = useQuery({ queryKey: ['users', 'directory'], queryFn: () => api.get<Directory>('/api/users/directory'), staleTime: 60_000 });
  const map = useMemo(() => new Map((dir.data?.items ?? []).map((u) => [u.id, pick(i18n.language, u.display_name_en, u.display_name_ar)])), [dir.data, i18n.language]);
  return (id: string) => map.get(id) ?? id.slice(0, 8);
}
